import {
  MAX_CANVAS_DIMENSION,
  MAX_CANVAS_PIXELS,
  getPatternMetrics,
  normalizeScreenName,
  validateConfig,
} from './config';
import type {
  CanvasAnnotations,
  CanvasDocument,
  CanvasItem,
  CanvasItemBounds,
  CanvasRotation,
  CanvasType,
  GeneratorConfig,
} from './types';

export const CANVAS_PRESETS = [
  { label: 'Custom', width: 0, height: 0 },
  { label: 'Full HD', width: 1920, height: 1080 },
  { label: 'UHD 4K', width: 3840, height: 2160 },
  { label: 'DCI 4K', width: 4096, height: 2160 },
] as const;

const DEFAULT_ANNOTATIONS: CanvasAnnotations = {
  showName: false,
  showCoordinates: true,
  showResolution: false,
};

export const DEFAULT_CANVAS: CanvasDocument = createDefaultCanvas('led');

export interface CanvasWarnings {
  outsideIds: Set<string>;
  overlapIds: Set<string>;
}

export interface ProjectionBlendZone extends CanvasItemBounds {
  id: string;
  itemIds: string[];
  itemNames: string[];
}

export interface SnapResult {
  x: number;
  y: number;
  guideX: number | null;
  guideY: number | null;
}

export interface CursorSnapResult {
  x: number;
  y: number;
  snapped: boolean;
}

export type CanvasClipboardAction = 'copy' | 'paste' | 'duplicate';
export type CanvasDistributionAxis = 'horizontal' | 'vertical';

export interface CanvasDistributionResult {
  canvas: CanvasDocument;
  overlaps: number[];
}

interface CanvasShortcutInput {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
}

export function createDefaultCanvas(canvasType: CanvasType): CanvasDocument {
  return {
    schemaVersion: 2,
    documentType: 'canvas',
    canvasType,
    title: canvasType === 'led' ? 'LED Canvas' : 'Projection Canvas',
    resolution: { width: 3840, height: 2160 },
    background: 'black',
    annotations: { ...DEFAULT_ANNOTATIONS },
    items: [],
  };
}

export function cloneDefaultCanvas(canvasType: CanvasType = 'led'): CanvasDocument {
  return structuredClone(createDefaultCanvas(canvasType));
}

export function normalizeCanvasItemConfig(config: GeneratorConfig): GeneratorConfig {
  const validated = validateConfig(config);
  if (validated.mode !== 'led') return structuredClone(validated);
  return {
    ...structuredClone(validated),
    power: { ...structuredClone(validated.power), routes: [] },
  };
}

export function createCanvasItem(
  config: GeneratorConfig,
  existingItems: CanvasItem[] = [],
  position?: { x: number; y: number },
): CanvasItem {
  const itemConfig = normalizeCanvasItemConfig(config);
  const name = uniqueItemName(normalizeScreenName(itemConfig.title), existingItems);
  return {
    id: createId(),
    name,
    kind: itemConfig.mode,
    x: Math.round(position?.x ?? existingItems.length * 32),
    y: Math.round(position?.y ?? existingItems.length * 32),
    rotation: 0,
    config: { ...itemConfig, title: name },
  };
}

export function applyCanvasItemConfig(item: CanvasItem, config: GeneratorConfig): CanvasItem {
  return {
    ...item,
    name: normalizeScreenName(config.title),
    config,
  };
}

export function renameCanvasItem(item: CanvasItem, nameDraft: string): CanvasItem {
  const name = normalizeScreenName(nameDraft);
  return { ...item, name, config: { ...item.config, title: name } };
}

export function copyCanvasItem(
  source: CanvasItem,
  existingItems: CanvasItem[] = [],
  position: { x: number; y: number } = { x: source.x + 32, y: source.y + 32 },
): CanvasItem {
  const copy = createCanvasItem(source.config, existingItems, position);
  copy.rotation = source.rotation;
  return copy;
}

export function getCanvasClipboardAction({
  key,
  ctrlKey,
  metaKey,
  altKey,
}: CanvasShortcutInput): CanvasClipboardAction | null {
  if ((!ctrlKey && !metaKey) || altKey) return null;
  if (key.toLowerCase() === 'c') return 'copy';
  if (key.toLowerCase() === 'v') return 'paste';
  if (key.toLowerCase() === 'd') return 'duplicate';
  return null;
}

export function getCanvasItemBounds(item: CanvasItem): CanvasItemBounds {
  const { resolution } = getPatternMetrics(item.config);
  const rotated = item.rotation === 90 || item.rotation === 270;
  return {
    x: item.x,
    y: item.y,
    width: rotated ? resolution.height : resolution.width,
    height: rotated ? resolution.width : resolution.height,
  };
}

export function distributeProjectionItems(
  canvas: CanvasDocument,
  selectedIds: Iterable<string>,
  axis: CanvasDistributionAxis,
): CanvasDistributionResult {
  const selectedIdSet = new Set(selectedIds);
  if (canvas.canvasType !== 'projection' || selectedIdSet.size < 2) {
    return { canvas, overlaps: [] };
  }

  const orderById = new Map(canvas.items.map((item, index) => [item.id, index]));
  const selected = canvas.items
    .filter((item) => selectedIdSet.has(item.id) && item.kind === 'projection')
    .map((item) => ({ item, bounds: getCanvasItemBounds(item) }))
    .sort((left, right) => {
      const positionDifference =
        axis === 'horizontal' ? left.bounds.x - right.bounds.x : left.bounds.y - right.bounds.y;
      return (
        positionDifference ||
        (orderById.get(left.item.id) ?? 0) - (orderById.get(right.item.id) ?? 0)
      );
    });

  if (selected.length < 2) return { canvas, overlaps: [] };

  const canvasSize = axis === 'horizontal' ? canvas.resolution.width : canvas.resolution.height;
  const sizes = selected.map(({ bounds }) =>
    axis === 'horizontal' ? bounds.width : bounds.height,
  );
  const intervalCount = selected.length - 1;
  const totalOverlap = sizes.reduce((sum, size) => sum + size, 0) - canvasSize;
  const baseOverlap = Math.floor(totalOverlap / intervalCount);
  const remainder = totalOverlap - baseOverlap * intervalCount;
  const overlaps = Array.from(
    { length: intervalCount },
    (_, index) => baseOverlap + (index < remainder ? 1 : 0),
  );
  const positions = new Map<string, number>();
  let position = 0;

  selected.forEach(({ item }, index) => {
    positions.set(item.id, position);
    if (index < intervalCount) position += sizes[index]! - overlaps[index]!;
  });

  return {
    canvas: {
      ...canvas,
      items: canvas.items.map((item) => {
        const nextPosition = positions.get(item.id);
        if (nextPosition === undefined) return item;
        return axis === 'horizontal' ? { ...item, x: nextPosition } : { ...item, y: nextPosition };
      }),
    },
    overlaps,
  };
}

export function getCanvasWarnings(canvas: CanvasDocument): CanvasWarnings {
  const outsideIds = new Set<string>();
  const overlapIds = new Set<string>();
  const bounds = canvas.items.map((item) => ({ item, bounds: getCanvasItemBounds(item) }));

  for (const entry of bounds) {
    const right = entry.bounds.x + entry.bounds.width;
    const bottom = entry.bounds.y + entry.bounds.height;
    if (
      entry.bounds.x < 0 ||
      entry.bounds.y < 0 ||
      right > canvas.resolution.width ||
      bottom > canvas.resolution.height
    ) {
      outsideIds.add(entry.item.id);
    }
  }

  for (let leftIndex = 0; leftIndex < bounds.length; leftIndex += 1) {
    const left = bounds[leftIndex];
    if (!left) continue;
    for (let rightIndex = leftIndex + 1; rightIndex < bounds.length; rightIndex += 1) {
      const right = bounds[rightIndex];
      if (!right || !rectanglesOverlap(left.bounds, right.bounds)) continue;
      overlapIds.add(left.item.id);
      overlapIds.add(right.item.id);
    }
  }

  return { outsideIds, overlapIds };
}

export function getProjectionBlendZones(canvas: CanvasDocument): ProjectionBlendZone[] {
  if (canvas.canvasType !== 'projection') return [];
  const entries = canvas.items.map((item) => ({ item, bounds: getCanvasItemBounds(item) }));
  const yEdges = uniqueSortedEdges(
    entries.flatMap(({ bounds }) => [bounds.y, bounds.y + bounds.height]),
  );
  const horizontalZones: Array<ProjectionBlendZone & { signature: string }> = [];

  for (let yIndex = 0; yIndex < yEdges.length - 1; yIndex += 1) {
    const y = yEdges[yIndex];
    const edgeY = yEdges[yIndex + 1];
    if (y === undefined || edgeY === undefined) continue;
    const events = entries
      .map(({ bounds }, index) => ({ bounds, index }))
      .filter(({ bounds }) => bounds.y <= y && bounds.y + bounds.height >= edgeY)
      .flatMap(({ bounds, index }) => [
        { x: bounds.x, index, entering: true },
        { x: bounds.x + bounds.width, index, entering: false },
      ])
      .sort((left, right) => left.x - right.x);
    const activeIndexes = new Set<number>();
    let previousX: number | null = null;

    for (let eventIndex = 0; eventIndex < events.length;) {
      const x = events[eventIndex]?.x;
      if (x === undefined) break;
      if (previousX !== null && x > previousX && activeIndexes.size >= 2) {
        const coveringIndexes = [...activeIndexes].sort((left, right) => left - right);
        const itemIds = coveringIndexes.map((index) => entries[index]!.item.id);
        const itemNames = coveringIndexes.map((index) => entries[index]!.item.name);
        const signature = itemIds.join('\u0000');
        const previous = horizontalZones.at(-1);
        if (
          previous &&
          previous.y === y &&
          previous.height === edgeY - y &&
          previous.x + previous.width === previousX &&
          previous.signature === signature
        ) {
          previous.width += x - previousX;
        } else {
          horizontalZones.push({
            id: '',
            itemIds,
            itemNames,
            x: previousX,
            y,
            width: x - previousX,
            height: edgeY - y,
            signature,
          });
        }
      }

      while (eventIndex < events.length && events[eventIndex]?.x === x) {
        const event = events[eventIndex];
        if (event?.entering) activeIndexes.add(event.index);
        else if (event) activeIndexes.delete(event.index);
        eventIndex += 1;
      }
      previousX = x;
    }
  }

  const mergedZones: Array<ProjectionBlendZone & { signature: string }> = [];
  for (const zone of horizontalZones) {
    const previous = mergedZones.find(
      (candidate) =>
        candidate.signature === zone.signature &&
        candidate.x === zone.x &&
        candidate.width === zone.width &&
        candidate.y + candidate.height === zone.y,
    );
    if (previous) {
      previous.height += zone.height;
    } else {
      mergedZones.push({ ...zone });
    }
  }

  return mergedZones.map((zone) => ({
    id: `${zone.itemIds.join(':')}@${zone.x},${zone.y}`,
    itemIds: zone.itemIds,
    itemNames: zone.itemNames,
    x: zone.x,
    y: zone.y,
    width: zone.width,
    height: zone.height,
  }));
}

export function snapCanvasItem(
  canvas: CanvasDocument,
  itemId: string,
  rawX: number,
  rawY: number,
  tolerance: number,
): SnapResult {
  const moving = canvas.items.find((item) => item.id === itemId);
  if (!moving) return { x: Math.round(rawX), y: Math.round(rawY), guideX: null, guideY: null };
  const movingBounds = getCanvasItemBounds({ ...moving, x: rawX, y: rawY });
  const xCandidates = [
    { position: 0, guide: 0 },
    {
      position: canvas.resolution.width - movingBounds.width,
      guide: canvas.resolution.width,
    },
  ];
  const yCandidates = [
    { position: 0, guide: 0 },
    {
      position: canvas.resolution.height - movingBounds.height,
      guide: canvas.resolution.height,
    },
  ];

  for (const item of canvas.items) {
    if (item.id === itemId) continue;
    const bounds = getCanvasItemBounds(item);
    xCandidates.push(
      { position: bounds.x, guide: bounds.x },
      { position: bounds.x + bounds.width, guide: bounds.x + bounds.width },
      { position: bounds.x - movingBounds.width, guide: bounds.x },
      {
        position: bounds.x + bounds.width - movingBounds.width,
        guide: bounds.x + bounds.width,
      },
    );
    yCandidates.push(
      { position: bounds.y, guide: bounds.y },
      { position: bounds.y + bounds.height, guide: bounds.y + bounds.height },
      { position: bounds.y - movingBounds.height, guide: bounds.y },
      {
        position: bounds.y + bounds.height - movingBounds.height,
        guide: bounds.y + bounds.height,
      },
    );
  }

  const snappedX = closestSnap(rawX, xCandidates, tolerance);
  const snappedY = closestSnap(rawY, yCandidates, tolerance);
  return {
    x: Math.round(snappedX?.position ?? rawX),
    y: Math.round(snappedY?.position ?? rawY),
    guideX: snappedX?.guide ?? null,
    guideY: snappedY?.guide ?? null,
  };
}

export function snapCanvasCursorToItemCorner(
  canvas: CanvasDocument,
  rawX: number,
  rawY: number,
  tolerance: number,
): CursorSnapResult {
  let closest: CursorSnapResult | null = null;
  let closestDistance = Math.max(0, tolerance) + Number.EPSILON;

  for (const item of canvas.items) {
    const bounds = getCanvasItemBounds(item);
    const corners = [
      { x: bounds.x, y: bounds.y },
      { x: bounds.x + bounds.width, y: bounds.y },
      { x: bounds.x, y: bounds.y + bounds.height },
      { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
    ];

    for (const corner of corners) {
      const distance = Math.hypot(corner.x - rawX, corner.y - rawY);
      if (distance >= closestDistance) continue;
      closest = { ...corner, snapped: true };
      closestDistance = distance;
    }
  }

  return closest ?? { x: Math.floor(rawX), y: Math.floor(rawY), snapped: false };
}

export function rotateCanvasItem(item: CanvasItem, direction: -1 | 1): CanvasItem {
  const rotations: CanvasRotation[] = [0, 90, 180, 270];
  const currentIndex = rotations.indexOf(item.rotation);
  const nextIndex = (currentIndex + direction + rotations.length) % rotations.length;
  return { ...item, rotation: rotations[nextIndex] ?? 0 };
}

export function validateCanvasDocument(input: unknown): CanvasDocument {
  if (!input || typeof input !== 'object') throw new Error('The Canvas project must be an object.');
  const value = input as Record<string, unknown>;
  if (value.documentType !== 'canvas' || (value.schemaVersion !== 1 && value.schemaVersion !== 2)) {
    throw new Error('Unsupported Canvas project format.');
  }

  const canvasType = value.schemaVersion === 1 ? 'led' : value.canvasType;
  if (canvasType !== 'led' && canvasType !== 'projection') {
    throw new Error('Invalid Canvas type.');
  }
  const resolution = value.resolution as { width?: unknown; height?: unknown } | undefined;
  if (!resolution || !isPositiveInteger(resolution.width)) {
    throw new Error('Canvas width must be a positive whole number.');
  }
  if (!isPositiveInteger(resolution.height)) {
    throw new Error('Canvas height must be a positive whole number.');
  }
  assertCanvasSize(resolution.width, resolution.height);
  if (value.background !== 'black' && value.background !== 'transparent') {
    throw new Error('Invalid Canvas background.');
  }
  if (typeof value.title !== 'string' || value.title.length > 120) {
    throw new Error('Canvas title must be text up to 120 characters.');
  }

  const rawItems = value.schemaVersion === 1 ? value.screens : value.items;
  if (!Array.isArray(rawItems) || rawItems.length > 200) {
    throw new Error('Canvas must contain no more than 200 items.');
  }
  const annotations = validateAnnotations(value.annotations, value.schemaVersion === 1);
  const ids = new Set<string>();
  const items = rawItems.map((rawItem, index) => {
    if (!rawItem || typeof rawItem !== 'object') throw new Error(`Invalid item ${index + 1}.`);
    const item = rawItem as Record<string, unknown>;
    if (typeof item.id !== 'string' || !item.id || item.id.length > 128) {
      throw new Error(`Invalid item ${index + 1} ID.`);
    }
    if (ids.has(item.id)) throw new Error('Canvas item IDs must be unique.');
    ids.add(item.id);
    if (typeof item.name !== 'string' || !item.name.trim() || item.name.length > 120) {
      throw new Error(`Item ${index + 1} must have a name.`);
    }
    if (!Number.isSafeInteger(item.x) || !Number.isSafeInteger(item.y)) {
      throw new Error(`Item ${index + 1} coordinates must be whole numbers.`);
    }
    if (![0, 90, 180, 270].includes(Number(item.rotation))) {
      throw new Error(`Item ${index + 1} has an invalid rotation.`);
    }
    const config = normalizeCanvasItemConfig(item.config as GeneratorConfig);
    const rawKind = value.schemaVersion === 1 ? 'led' : item.kind;
    if (rawKind !== 'led' && rawKind !== 'projection') {
      throw new Error(`Item ${index + 1} has an invalid type.`);
    }
    const kind: CanvasType = rawKind;
    if (kind !== config.mode || kind !== canvasType) {
      throw new Error('Canvas items must match the Canvas type.');
    }
    return {
      id: item.id,
      name: item.name,
      kind,
      x: item.x as number,
      y: item.y as number,
      rotation: item.rotation as CanvasRotation,
      config: { ...config, title: item.name },
    };
  });

  return {
    schemaVersion: 2,
    documentType: 'canvas',
    canvasType,
    title: value.title,
    resolution: { width: resolution.width, height: resolution.height },
    background: value.background,
    annotations,
    items,
  };
}

export function buildCanvasFileName(canvas: CanvasDocument): string {
  const fallback = canvas.canvasType === 'led' ? 'LED_Canvas' : 'Projection_Canvas';
  const title =
    canvas.title
      .trim()
      .replace(/[^a-zA-Z0-9._-]+/g, '_')
      .replace(/^_+|_+$/g, '') || fallback;
  return `${title}_${canvas.resolution.width}x${canvas.resolution.height}.png`;
}

function validateAnnotations(input: unknown, legacy: boolean): CanvasAnnotations {
  if (legacy || input === undefined) return { ...DEFAULT_ANNOTATIONS };
  if (!input || typeof input !== 'object') throw new Error('Invalid Canvas annotations.');
  const value = input as Record<string, unknown>;
  if (
    typeof value.showName !== 'boolean' ||
    typeof value.showCoordinates !== 'boolean' ||
    typeof value.showResolution !== 'boolean'
  ) {
    throw new Error('Invalid Canvas annotations.');
  }
  return {
    showName: value.showName,
    showCoordinates: value.showCoordinates,
    showResolution: value.showResolution,
  };
}

function rectanglesOverlap(left: CanvasItemBounds, right: CanvasItemBounds): boolean {
  return (
    left.x < right.x + right.width &&
    left.x + left.width > right.x &&
    left.y < right.y + right.height &&
    left.y + left.height > right.y
  );
}

function uniqueSortedEdges(values: number[]): number[] {
  return [...new Set(values)].sort((left, right) => left - right);
}

function closestSnap(
  value: number,
  candidates: { position: number; guide: number }[],
  tolerance: number,
): { position: number; guide: number } | null {
  let closest: { position: number; guide: number } | null = null;
  let distance = Math.max(0, tolerance) + Number.EPSILON;
  for (const candidate of candidates) {
    const candidateDistance = Math.abs(candidate.position - value);
    if (candidateDistance >= distance) continue;
    closest = candidate;
    distance = candidateDistance;
  }
  return closest;
}

function uniqueItemName(base: string, items: CanvasItem[]): string {
  const used = new Set(items.map((item) => item.name.toLocaleLowerCase()));
  if (!used.has(base.toLocaleLowerCase())) return base;
  let number = 2;
  while (used.has(`${base} ${number}`.toLocaleLowerCase())) number += 1;
  return `${base} ${number}`;
}

function isPositiveInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) > 0;
}

function assertCanvasSize(width: number, height: number): void {
  if (width > MAX_CANVAS_DIMENSION || height > MAX_CANVAS_DIMENSION) {
    throw new Error(`Canvas cannot exceed ${MAX_CANVAS_DIMENSION} px per side.`);
  }
  if (width * height > MAX_CANVAS_PIXELS) {
    throw new Error('Canvas is too large for browser rendering.');
  }
}

function createId(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `item-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
